import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, type Observable, ReplaySubject, Subject } from 'rxjs';

import { type ReportChatAttachment, allowedAttachments, toAttachments, toMessageFiles } from '../attachments';
import { type ReportChatMessage } from '../interfaces';
import {
  REPORT_CHAT_AUTH_TOKEN,
  REPORT_CHAT_BEARER_SUBPROTOCOL,
  REPORT_CHAT_SOCKET_BASE_URL,
  type ReportChatClientFrame,
  type ReportChatConnectOptions,
  type ReportChatServerFrame,
  type ReportChatSession,
  type ReportChatSessionError,
  type ReportChatSocketStatus,
  type ReportChatSocketViewerRole,
} from './report-chat-socket.interfaces';

/** Backoff schedule (ms) for reconnect attempts; last value is the ceiling. */
const RECONNECT_DELAYS_MS = [500, 1000, 2000, 5000, 10_000];
/** Random extra delay added to each reconnect to spread herd reconnects. */
const RECONNECT_JITTER_MS = 400;
/**
 * Give up after this many consecutive failed reconnects (no `onopen` in
 * between) so a permanently-broken endpoint is not hammered forever; the session
 * goes `closed` and the UI prompts a manual refresh.
 */
export const MAX_RECONNECT_ATTEMPTS = 10;
/** Normal close — do not reconnect. */
const WS_CLOSE_NORMAL = 1000;
/** Auth/forbidden closes — reconnect cannot help, surface as closed. */
const WS_CLOSE_UNAUTHORIZED = 4401;
const WS_CLOSE_FORBIDDEN = 4403;

/**
 * Factory the service uses to open the underlying socket. Overridable in tests
 * (the spec injects a fake socket) — production binds the native constructor.
 * `protocols` carries the `bearer` auth subprotocol when a token is available.
 */
export type ReportChatSocketFactory = (url: string, protocols?: string[]) => WebSocket;

const DEFAULT_SOCKET_FACTORY: ReportChatSocketFactory = (url, protocols) => new WebSocket(url, protocols);

/**
 * A `system` frame's `body` is a JSON `{key, params}` i18n payload. Parse it
 * defensively: a malformed or non-JSON body yields null so the caller keeps the
 * raw body instead of crashing the whole frame batch.
 */
function parseSystemBody(body: string): { key: string; params: Record<string, string | number> } | null {
  try {
    const parsed: unknown = JSON.parse(body);
    if (parsed && typeof parsed === 'object' && typeof (parsed as { key?: unknown }).key === 'string') {
      const { key, params } = parsed as { key: string; params?: Record<string, string | number> };
      return { key, params: params ?? {} };
    }
  } catch {
    // Not JSON — fall through to null.
  }
  return null;
}

/**
 * One live `wss://…/ws/report-chat/<id>` session. Holds server rows in a
 * `Map<id>` and unacked optimistic rows in a `Map<tempId>`; the merged,
 * id-sorted, deduped view is published on {@link messages$}. Reconnects with
 * jittered backoff and replays from the highest id seen via `?after=`.
 */
class ReportChatSessionImpl implements ReportChatSession {
  private readonly server = new Map<number, ReportChatMessage>();
  private readonly optimistic = new Map<string, ReportChatMessage>();
  private readonly messagesSubject = new BehaviorSubject<ReportChatMessage[]>([]);
  private readonly statusSubject = new BehaviorSubject<ReportChatSocketStatus>('connecting');
  private readonly errorsSubject = new Subject<ReportChatSessionError>();
  /**
   * Failed state of the LATEST send only — see {@link ReportChatSession.sendFailed$}.
   * Reset on every send/retry, flipped on that send's error/mid-flight drop,
   * cleared on that send's ack. Never driven by older failed bubbles.
   */
  private readonly sendFailedSubject = new BehaviorSubject<boolean>(false);
  /** tempId of the most recent send/retry — the only one `sendFailed$` reflects. */
  private lastSendTempId: string | undefined;
  /**
   * Read cursor from the server `read_state` frame (null = nothing read). A
   * ReplaySubject (not BehaviorSubject) so it emits ONLY when `read_state`
   * actually arrives — never a synthetic initial value the host could mistake
   * for a real "nothing read" cursor. Replays to a late subscriber.
   */
  private readonly readCursorSubject = new ReplaySubject<number | null>(1);
  /**
   * The report's LLM title, pushed by the server once generated (async, after
   * submit). ReplaySubject(1) so a subscriber that attaches after the frame
   * still gets the latest title. Emits only on a real `title` frame.
   */
  private readonly reportTitleSubject = new ReplaySubject<string>(1);

  private socket: WebSocket | undefined;
  /** Highest server id seen — the `after` cursor for the next (re)connect. */
  private lastSeenId: number;
  /** Frames queued while the socket is not yet open; flushed on open. */
  private readonly outbox: ReportChatClientFrame[] = [];
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  private closedByCaller = false;
  /** Terminal state: caller closed, or reconnect gave up. No socket will reopen. */
  private terminated = false;

  readonly messages$: Observable<ReportChatMessage[]> = this.messagesSubject.asObservable();
  readonly status$: Observable<ReportChatSocketStatus> = this.statusSubject.asObservable();
  readonly errors$: Observable<ReportChatSessionError> = this.errorsSubject.asObservable();
  readonly sendFailed$: Observable<boolean> = this.sendFailedSubject.asObservable();
  readonly readCursor$: Observable<number | null> = this.readCursorSubject.asObservable();
  readonly reportTitle$: Observable<string> = this.reportTitleSubject.asObservable();

  constructor(
    private readonly baseUrl: string,
    private readonly reportId: number,
    private readonly viewerRole: ReportChatSocketViewerRole,
    afterId: number | undefined,
    private readonly socketFactory: ReportChatSocketFactory,
    private readonly tokenProvider: () => string | undefined,
  ) {
    this.lastSeenId = afterId ?? 0;
    this.open();
  }

  send(body: string, attachments?: ReportChatAttachment[]): void {
    const trimmed = body.trim();
    // Attachment URLs are checked once, here: the optimistic row, the emptiness
    // rule and the wire frame must all be about the same files. A rejected URL
    // is not "a file we could not draw" — it is a file that does not exist for
    // this send, so an attachment-only send made entirely of them stays a no-op.
    const files = allowedAttachments(attachments);
    // An attachment alone is a valid message (a screenshot with no words).
    if (!trimmed && !files.length) {
      return;
    }
    const tempId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const optimistic: ReportChatMessage = {
      id: tempId,
      authorRole: this.viewerRole,
      body: trimmed,
      createdAt: new Date().toISOString(),
      mine: true,
      files: toMessageFiles(files),
      pending: !this.terminated,
      // On a terminated session there is no socket to deliver this, so surface it
      // as failed immediately (retryable once the user reconnects) rather than
      // leaving it stuck pending with no ack/error ever coming.
      failed: this.terminated,
      seenByStaff: this.viewerRole === 'student' ? false : undefined,
    };
    this.optimistic.set(tempId, optimistic);
    // This is now the send the global banner reflects: reset to its own outcome.
    // On a live session it starts not-failed (pending); on a terminated one the
    // row is born failed, so the banner must light up for this send right away.
    this.lastSendTempId = tempId;
    this.sendFailedSubject.next(this.terminated);
    this.emit();
    if (!this.terminated) {
      this.enqueue(this.buildFrame(tempId, trimmed, files));
    }
  }

  retry(message: ReportChatMessage): void {
    if (this.terminated) {
      return;
    }
    const tempId = String(message.id);
    const existing = this.optimistic.get(tempId);
    if (!existing) {
      return;
    }
    this.optimistic.set(tempId, { ...existing, pending: true, failed: false });
    // A retry is the latest send: the banner now tracks this attempt, cleared
    // until it acks or fails on its own.
    this.lastSendTempId = tempId;
    this.sendFailedSubject.next(false);
    this.emit();
    // The optimistic row is the only place the attachment list survives a failed
    // send, so the retry frame is rebuilt from its rendered files.
    this.enqueue(this.buildFrame(tempId, existing.body, toAttachments(existing.files)));
  }

  markRead(upToId: number): void {
    // Fire-and-forget: only meaningful on an open socket. A missed read frame is
    // harmless — the next reach-bottom re-sends, and the server keeps the cursor
    // monotonic. Not queued into the outbox (that is for message sends).
    if (this.terminated || !Number.isFinite(upToId)) {
      return;
    }
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ type: 'read', upToId }));
    }
  }

  close(): void {
    this.closedByCaller = true;
    this.terminated = true;
    this.clearReconnectTimer();
    this.teardownSocket(WS_CLOSE_NORMAL);
    this.statusSubject.next('closed');
  }

  private open(): void {
    this.clearReconnectTimer();
    const url = this.buildUrl();
    this.statusSubject.next(this.reconnectAttempt === 0 ? 'connecting' : 'reconnecting');

    // Browser WebSockets cannot set an Authorization header and a many-roles JWT
    // overflows the cookie, so carry the token as the `bearer` subprotocol. When
    // no token is available, connect without one — the server closes with 4401.
    const token = this.tokenProvider();
    const protocols = token ? [REPORT_CHAT_BEARER_SUBPROTOCOL, token] : undefined;
    try {
      this.socket = this.socketFactory(url, protocols);
    } catch {
      this.scheduleReconnect();
      return;
    }

    this.socket.onopen = (): void => {
      this.reconnectAttempt = 0;
      this.statusSubject.next('open');
      this.flushOutbox();
    };
    this.socket.onmessage = (event: MessageEvent): void => this.handleRawFrame(event.data);
    this.socket.onerror = (): void => {
      // `onclose` always follows `onerror`; reconnect is driven from there.
    };
    this.socket.onclose = (event: CloseEvent): void => this.handleClose(event.code);
  }

  private buildUrl(): string {
    const wsBase = this.baseUrl.replace(/^http/i, 'ws');
    const after = this.lastSeenId > 0 ? `?after=${this.lastSeenId}` : '';
    return `${wsBase}/ws/report-chat/${this.reportId}${after}`;
  }

  private handleRawFrame(raw: unknown): void {
    if (typeof raw !== 'string') {
      return;
    }
    let frame: ReportChatServerFrame;
    try {
      frame = JSON.parse(raw) as ReportChatServerFrame;
    } catch {
      return;
    }
    this.handleFrame(frame);
  }

  private handleFrame(frame: ReportChatServerFrame): void {
    switch (frame.type) {
      case 'message':
        this.upsertServer(frame);
        break;
      case 'ack': {
        // Promote the optimistic row to a confirmed server row keyed on the
        // acked id — do NOT just drop it waiting for the broadcast echo. The
        // backend publish is best-effort, so if Redis is briefly down the echo
        // may be delayed (until the safety sweep) or this socket may already be
        // subscribed and get it instantly; promoting here keeps the message on
        // screen regardless, and the real echo (same id) dedups by id.
        const pending = this.optimistic.get(frame.tempId);
        this.optimistic.delete(frame.tempId);
        if (pending && !this.server.has(frame.id)) {
          this.server.set(frame.id, { ...pending, id: frame.id, pending: false, failed: false });
        }
        this.lastSeenId = Math.max(this.lastSeenId, frame.id);
        // The latest send landed — drop the global banner regardless of any
        // older failed bubble still sitting in the thread.
        if (frame.tempId === this.lastSendTempId) {
          this.sendFailedSubject.next(false);
        }
        this.emit();
        break;
      }
      case 'read_state':
        // The viewer's read cursor as it stood before this session; the host
        // anchors + freezes the "new messages" divider from it.
        this.readCursorSubject.next(frame.cursor);
        break;
      case 'title':
        // The report's LLM title just became available — push it up so the
        // history card / header updates live instead of only on the next fetch.
        this.reportTitleSubject.next(frame.title);
        break;
      case 'error':
        if (frame.tempId) {
          this.markFailed(frame.tempId);
          // Claim-on-reply conflicts (403 assigned-to-another / 409 retry) DO
          // carry a tempId (they come from the send path), but they are also
          // thread-level: the operator needs the specific reason banner AND an
          // assignee refresh, not just a generic failed bubble. Surface those on
          // errors$ too. Plain send failures (422 body / 429 rate-limit) stay on
          // the failed bubble + generic banner only.
          if (frame.code === 403 || frame.code === 409) {
            this.errorsSubject.next({ code: frame.code, message: frame.message });
          }
        } else {
          // Thread-level error not tied to a send — e.g. claim-on-reply 403/409.
          // Surface it so the operator UI reacts instead of silently dropping it.
          this.errorsSubject.next({ code: frame.code, message: frame.message });
          // Defensive: our backend always carries a tempId on send-path errors
          // (claim 403/409 come with one, so markFailed clears pending). But if a
          // tempId-less error ever lands while a send is still in flight, do not
          // leave that optimistic row stuck pending forever — fail the most recent
          // still-pending one so the user can retry. No-op when nothing is pending.
          this.failLatestPendingOptimistic();
        }
        break;
    }
  }

  private upsertServer(frame: Extract<ReportChatServerFrame, { type: 'message' }>): void {
    // A genuinely-new id is the only one that can be the persisted echo of an
    // orphaned optimistic. The catch-up contract re-delivers already-seen ids on
    // `?after=` overlap, so an overlap echo is NOT evidence of a fresh persist.
    const isNewId = !this.server.has(frame.id);
    // A `system` frame carries no plain text: its `body` is a JSON `{key, params}`
    // i18n payload. Lift it into `systemKey`/`systemParams` (the host localizes it
    // into `systemText`) and blank `body` so an unlocalized notice never leaks the
    // raw JSON into a bubble.
    const systemPayload = frame.authorRole === 'system' ? parseSystemBody(frame.body) : null;
    this.server.set(frame.id, {
      id: frame.id,
      authorRole: frame.authorRole,
      body: systemPayload ? '' : frame.body,
      createdAt: frame.createdAt,
      mine: frame.mine,
      seenByStaff: frame.seenByStaff,
      files: toMessageFiles(frame.attachments),
      ...(systemPayload && { systemKey: systemPayload.key, systemParams: systemPayload.params }),
    });
    this.lastSeenId = Math.max(this.lastSeenId, frame.id);
    // Reconcile a replayed echo of OUR OWN send against its orphaned optimistic
    // row. On a mid-flight drop the ack never arrives, so the optimistic row
    // (now `failed`, or still `pending` if a safety-sweep echo beat the ack)
    // cannot be promoted by tempId. Without this, the reconnect replay surfaces
    // the persisted row alongside the stale bubble — the message shows twice,
    // and retrying the bubble re-sends a server-side duplicate. Match the echo
    // to the oldest same-body optimistic of ours and drop it — but ONLY for a
    // genuinely-new id, so an overlap re-delivery of an old row cannot drop a
    // newer same-body send the user has in flight.
    if (isNewId && frame.mine === true) {
      this.dropOptimisticMatching(frame.body);
    }
    this.emit();
  }

  /**
   * Drop the oldest optimistic row whose body matches a confirmed own-message
   * echo — it has just been proven persisted on the server. Map iteration is
   * insertion-ordered, so the first match is the earliest send (FIFO), which is
   * the one the catch-up replay is echoing. If that row was the latest send, its
   * failed banner is now stale: clear it.
   */
  private dropOptimisticMatching(body: string): void {
    for (const [tempId, message] of this.optimistic) {
      if (message.body === body) {
        this.optimistic.delete(tempId);
        if (tempId === this.lastSendTempId) {
          this.sendFailedSubject.next(false);
        }
        return;
      }
    }
  }

  private markFailed(tempId: string): void {
    const existing = this.optimistic.get(tempId);
    if (!existing) {
      return;
    }
    this.optimistic.set(tempId, { ...existing, pending: false, failed: true });
    // Light the global banner only when it is the latest send that failed —
    // an error for an older retry must not re-raise it once a newer send acked.
    if (tempId === this.lastSendTempId) {
      this.sendFailedSubject.next(true);
    }
    this.emit();
  }

  /**
   * Fail the most recently inserted still-pending optimistic row, if any. Used
   * as a safety net for a hypothetical tempId-less `error` frame: it must not
   * leave an in-flight send stuck pending. Walks the optimistic map from the
   * newest entry (Map preserves insertion order) and fails the first pending one.
   */
  private failLatestPendingOptimistic(): void {
    const entries = Array.from(this.optimistic.entries());
    for (let index = entries.length - 1; index >= 0; index -= 1) {
      const [tempId, message] = entries[index];
      if (message.pending === true) {
        this.markFailed(tempId);
        return;
      }
    }
  }

  /**
   * Build a send frame, omitting `attachments` entirely for a plain text
   * message — see {@link ReportChatClientFrame}.
   *
   * Единственная точка сборки исходящего кадра, поэтому проверка URL стоит и
   * здесь: что бы ни принёс вызывающий (первая отправка, повтор упавшей строки,
   * будущий третий путь), на провод не уйдёт URL, который мы отказались рисовать.
   */
  private buildFrame(tempId: string, body: string, attachments?: ReportChatAttachment[]): ReportChatClientFrame {
    const files = allowedAttachments(attachments);
    return { tempId, body, ...(files.length ? { attachments: files } : {}) };
  }

  private enqueue(frame: ReportChatClientFrame): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(frame));
      return;
    }
    this.outbox.push(frame);
  }

  private flushOutbox(): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return;
    }
    const pending = this.outbox.splice(0, this.outbox.length);
    for (const frame of pending) {
      this.socket.send(JSON.stringify(frame));
    }
  }

  private handleClose(code: number): void {
    if (this.closedByCaller) {
      return;
    }
    // A send may have been in flight (sent on the open socket, no ack yet) when
    // the socket dropped. We cannot know whether the server persisted it and we
    // do not auto-resend (no server-side tempId dedup yet), so surface it as
    // failed — the user can retry — instead of leaving it stuck pending forever.
    this.failPendingOptimistic();
    if (code === WS_CLOSE_UNAUTHORIZED || code === WS_CLOSE_FORBIDDEN) {
      // Auth/forbidden is terminal — a reconnect cannot recover it. Mark the
      // session terminated so send()/retry() fail fast instead of queueing into
      // an outbox that will never flush (no socket will reopen). Surface a
      // distinct `unauthorized` status, NOT `closed`, so the host shows an
      // auth/permission message instead of "connection lost" (the network is fine).
      this.terminated = true;
      this.statusSubject.next('unauthorized');
      return;
    }
    this.scheduleReconnect();
  }

  private failPendingOptimistic(): void {
    // Also drop the not-yet-sent outbox: those frames belong to the rows we are
    // about to fail, so flushing them on the next reconnect would silently send
    // a message the user already sees as failed (and could re-send by retry → a
    // server-side duplicate). The user re-sends explicitly via retry instead.
    this.outbox.length = 0;
    let changed = false;
    let latestFailed = false;
    for (const [tempId, message] of this.optimistic) {
      if (message.pending) {
        this.optimistic.set(tempId, { ...message, pending: false, failed: true });
        changed = true;
        if (tempId === this.lastSendTempId) {
          latestFailed = true;
        }
      }
    }
    // The latest send was in flight when the socket dropped — raise the banner.
    if (latestFailed) {
      this.sendFailedSubject.next(true);
    }
    if (changed) {
      this.emit();
    }
  }

  private scheduleReconnect(): void {
    if (this.closedByCaller) {
      return;
    }
    if (this.reconnectAttempt >= MAX_RECONNECT_ATTEMPTS) {
      // Endpoint looks permanently broken — stop hammering it; the UI prompts a
      // manual refresh off the `closed` status. Terminal: further sends fail fast.
      this.terminated = true;
      this.statusSubject.next('closed');
      return;
    }
    this.statusSubject.next('reconnecting');
    const base = RECONNECT_DELAYS_MS[Math.min(this.reconnectAttempt, RECONNECT_DELAYS_MS.length - 1)];
    const delay = base + Math.floor(Math.random() * RECONNECT_JITTER_MS);
    this.reconnectAttempt += 1;
    this.reconnectTimer = setTimeout(() => this.open(), delay);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== undefined) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }
  }

  private teardownSocket(code: number): void {
    if (!this.socket) {
      return;
    }
    this.socket.onopen = null;
    this.socket.onmessage = null;
    this.socket.onerror = null;
    this.socket.onclose = null;
    try {
      this.socket.close(code);
    } catch {
      // Already closing/closed — nothing to do.
    }
    this.socket = undefined;
  }

  /** Publish the merged view: server rows (id ASC) then unacked optimistic rows. */
  private emit(): void {
    const serverRows = Array.from(this.server.values()).sort(
      (left, right) => (left.id as number) - (right.id as number),
    );
    this.messagesSubject.next([...serverRows, ...this.optimistic.values()]);
  }
}

/**
 * Opens per-report support-chat WebSocket sessions against the proxy. One
 * session per open thread; the host container closes it on report switch /
 * destroy. Replaces the former HTTP poll; unread badges stay on HTTP.
 */
@Injectable({ providedIn: 'root' })
export class ReportChatSocketService {
  private readonly baseUrl = inject(REPORT_CHAT_SOCKET_BASE_URL);
  private readonly tokenProvider = inject(REPORT_CHAT_AUTH_TOKEN);
  private socketFactory: ReportChatSocketFactory = DEFAULT_SOCKET_FACTORY;

  /** Test seam: swap the socket constructor for a fake. */
  setSocketFactory(factory: ReportChatSocketFactory): void {
    this.socketFactory = factory;
  }

  connect(reportId: number, opts?: ReportChatConnectOptions): ReportChatSession {
    return new ReportChatSessionImpl(
      this.baseUrl,
      reportId,
      opts?.viewerRole ?? 'student',
      opts?.afterId,
      this.socketFactory,
      this.tokenProvider,
    );
  }
}
